/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, FormControl, Grid, MenuItem, Select, TextField, Typography } from '@mui/material';
import { InputAdornment } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import Spinner from 'modules/common/spinner.component';
import { formatDateForInput } from 'utilities/common';
import { EXPENSE_LINE_APPROVAL_STATUS, ZERO_GUID } from './expenseDetailsView.container';
import AdvancedSearchComponent from 'modules/common/advancedSearch.component';
import ExpenseAttachmentModal from 'modules/common/ExpenseAttachmentModal.component';
import { ExceptionId } from 'modules/authorizations/details/notesAndCommunicationEditableCard.component';
import SelectedCommentAndNotesComponent from 'modules/authorizations/details/commentsAndNotes/selectedCommentAndNotes.component';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';

const useStyles = makeStyles(theme => ({
  mb2: { marginBottom: '4px', marginRight: '16px' },
  iconOverlayImg: { position: 'absolute', zIndex: '1 !important' }
}));

const ReportLineItem = ({
  isReadOnly = false,
  lineItem,
  expenseCodes,
  enumerations,
  onSaveLineItem,
  closeLineItem,
  expenseDetail,
  updateParentExpenseAttachments,
  updateParentExpenseLineAttachments,
  exceptionSummaries,
  expenseLineItemExceptions,
  authId,
  setExceptions,
  pushToAcumatica,
  isAcumaticaRejected
}) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({ ...lineItem });
  const [isDirty, setIsDirty] = useState(false);

  const totalAmount = Number(form.totalAmount ?? 0);
  const approvedAmount = Number(form.approvedAmount ?? 0);
  const deniedAmount = Math.max(totalAmount - approvedAmount, 0);

  const statusOptions = useMemo(() => (enumerations?.lineItemStatuses ?? []).filter(s => s.isActive), [enumerations]);
  const [selectedExpenseLineItem, setSelectedExpenseLineItem] = useState(
    expenseLineItemExceptions && Array.isArray(expenseLineItemExceptions)
      ? expenseLineItemExceptions.find(e => e.lineItemId === lineItem.lineItemId)
      : null
  );

  let filteredExceptions =
    exceptionSummaries && Array.isArray(exceptionSummaries)
      ? exceptionSummaries.filter(es => isIdEqual(es.exceptionStatus, 'D71921DD-72F0-44E3-9F1D-ED9CF71CD285'))
      : [];

  const selectedExpenseExceptions =
    selectedExpenseLineItem && Array.isArray(filteredExceptions)
      ? filteredExceptions.find(es => es.exceptionId === selectedExpenseLineItem.authorizationExceptionCommentId)
      : null;

  const [openAddException, setOpenAddException] = useState(false);
  const [selectedExceptionId, setSelectedExceptionId] = useState(selectedExpenseExceptions?.exceptionId ?? 0);

  const isApprovedEnabled =
    (String(form.lineItemStatusId ?? '').toLowerCase() === String(EXPENSE_LINE_APPROVAL_STATUS.PARTIAL_APPROVED ?? '').toLowerCase() ||
      form.lineItemStatusId === ZERO_GUID) &&
    !isReadOnly;

  useEffect(() => {
    setSelectedExpenseLineItem(
      expenseLineItemExceptions && Array.isArray(expenseLineItemExceptions)
        ? expenseLineItemExceptions.find(e => e.lineItemId === lineItem.lineItemId)
        : null
    );
  }, [expenseLineItemExceptions, lineItem]);

  useEffect(() => {
    const jsonA = JSON.stringify(lineItem ?? {});
    const jsonB = JSON.stringify(form ?? {});
    setIsDirty(jsonA !== jsonB || selectedExpenseExceptions?.exceptionId !== selectedExceptionId);
  }, [form, lineItem, selectedExceptionId]);

  const setValue = patch => setForm(prev => ({ ...prev, ...patch }));

  const checkMoney = v => {
    const n = Number(v);
    if (Number.isNaN(n) || n < 0) return 0;
    return Math.round(n * 100) / 100;
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      let request = {
        expenseLineItemRow: { ...form },
        expenseLineItemExceptionRow: {
          exceptionCommentId: selectedExceptionId || 0,
          lineItemId: form?.lineItemId ?? 0,
          isVoucherLineItem: false,
          isExpenseLineItem: true
        }
      };
      const amt = checkMoney(request.expenseLineItemRow.totalAmount ?? 0);
      const appr = checkMoney(request.expenseLineItemRow.approvedAmount ?? 0);

      request.expenseLineItemRow.totalAmount = amt;
      request.expenseLineItemRow.approvedAmount = Math.min(appr, amt);

      if (
        String(request.expenseLineItemRow.lineItemStatusId ?? '').toLowerCase() ===
        String(EXPENSE_LINE_APPROVAL_STATUS.PARTIAL_APPROVED).toLowerCase()
      ) {
        if (request.expenseLineItemRow.approvedAmount === 0 && EXPENSE_LINE_APPROVAL_STATUS.DENIED) {
          request.expenseLineItemRow.lineItemStatusId = EXPENSE_LINE_APPROVAL_STATUS.DENIED;
        } else if (
          request.expenseLineItemRow.approvedAmount === request.expenseLineItemRow.totalAmount &&
          EXPENSE_LINE_APPROVAL_STATUS.APPROVED
        ) {
          request.expenseLineItemRow.lineItemStatusId = EXPENSE_LINE_APPROVAL_STATUS.APPROVED;
        }
      }

      await onSaveLineItem(request);
    } finally {
      setIsLoading(false);
    }
  };

  const payOptionsDisplay = ['Yes', 'No', 'N/A'];
  const toDisplayFromBool = b => (b == null ? 'N/A' : b ? 'Yes' : 'No');
  const toBoolFromDisplay = s => (s === 'Yes' ? true : s === 'No' ? false : null);

  const ec = form.expenseCodeId ? expenseCodes?.find(e => e.expenseCodeId === form.expenseCodeId) : null;

  return (
    <Box>
      <Grid container spacing={1} columns={{ xs: 22, sm: 22, md: 22, xl: 22 }}>
        <Grid item md={2}>
          <Select
            labelId={'approvalStatus'}
            key={'approvalStatus'}
            name={'approvalStatus'}
            value={form.lineItemStatusId ?? ''}
            sx={{ maxWidth: '140px', minWidth: '120px' }}
            disabled={isReadOnly}
            onChange={e => {
              let id = e.target.value;
              if (id === EXPENSE_LINE_APPROVAL_STATUS.APPROVED) {
                setValue({ approvedAmount: totalAmount, deniedAmount: 0 });
              } else if (id === EXPENSE_LINE_APPROVAL_STATUS.DENIED) {
                setValue({ approvedAmount: 0, deniedAmount: totalAmount });
              } else {
                setValue({ approvedAmount: lineItem.approvedAmount, deniedAmount: lineItem.deniedAmount });
              }

              setValue({ lineItemStatusId: id || null });
            }}
          >
            {statusOptions.map(opt => (
              <MenuItem disabled={isReadOnly} key={opt.id} value={opt.id} sx={{ margin: 'dense' }}>
                {opt.title}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item md={2}>
          <Box sx={{ paddingTop: '10px' }}>
            <div className={classes.mb2}>
              <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500 }}>
                {ec?.category ?? <div>&mdash;</div>}
              </Typography>
            </div>
          </Box>
        </Grid>
        <Grid item md={5}>
          <TextField
            key={'description'}
            name={'description'}
            disabled={isReadOnly}
            value={form.description ?? ''}
            sx={{ maxWidth: '500px' }}
            fullWidth
            InputLabelProps={{ shrink: true }}
            onChange={e => setValue({ description: e.target.value })}
          />
        </Grid>
        <Grid item md={2}>
          <Select
            labelId={'payOption'}
            key={'payOption'}
            name={'payOption'}
            value={toDisplayFromBool(form.payOption)}
            sx={{ maxWidth: '150px', minWidth: '120px' }}
            disabled={isReadOnly}
            onChange={e => setValue({ payOption: toBoolFromDisplay(e.target.value) })}
          >
            {payOptionsDisplay.map(opt => (
              <MenuItem disabled={isReadOnly} key={opt} value={opt} sx={{ margin: 'dense' }}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item md={2}>
          <TextField
            key={'totalAmount'}
            name={'totalAmount'}
            disabled={!isApprovedEnabled}
            value={isApprovedEnabled ? (Number.isNaN(totalAmount) ? '' : totalAmount) : totalAmount.toFixed(2)}
            sx={{ maxWidth: '500px' }}
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="number"
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            onChange={e => {
              let v = e.target.value;
              if (v === '' || (!Number.isNaN(Number(v)) && Number(v) >= 0)) {
                let total = v === '' ? 0 : Number(v);
                setValue({ totalAmount: total });
              }
            }}
          />
        </Grid>
        <Grid item md={2}>
          <TextField
            key={'approvedAmount'}
            name={'approvedAmount'}
            disabled={!isApprovedEnabled}
            value={isApprovedEnabled ? (approvedAmount === 0 ? '' : approvedAmount) : approvedAmount.toFixed(2)}
            sx={{ maxWidth: '500px' }}
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="number"
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            onChange={e => {
              let v = e.target.value;
              if (v === '' || (!Number.isNaN(Number(v)) && Number(v) >= 0)) {
                let appr = v === '' ? 0 : Number(v);
                if (appr > totalAmount) appr = totalAmount;
                setValue({ approvedAmount: appr });
              }
            }}
          />
        </Grid>
        <Grid item md={2}>
          <TextField
            key={'deniedAmount'}
            name={'deniedAmount'}
            disabled
            value={deniedAmount.toFixed(2)}
            sx={{ maxWidth: '500px' }}
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="number"
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          />
        </Grid>
        <Grid item md={2}>
          <ExpenseAttachmentModal
            displayText={isReadOnly ? 'View Attachments' : 'Add Attachment'}
            isReadOnly={isReadOnly}
            expenseLineInfo={expenseDetail.expenseInformation}
            expenseAttachments={expenseDetail.expenseAttachments}
            expenseLineAttachments={
              expenseDetail && expenseDetail.expenseLineAttachments
                ? expenseDetail.expenseLineAttachments.filter(ela => ela.expenseLineItemId === lineItem.lineItemId)
                : []
            }
            updateParentExpenseAttachments={updateParentExpenseAttachments}
            updateParentExpenseLineAttachments={updateParentExpenseLineAttachments}
            lineItemId={lineItem.lineItemId}
          />
        </Grid>
        <Grid item md={1}></Grid>
        <Grid item md={2}>
          <Box>
            <div className={classes.mb2}>
              <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400 }}>
                Reference
              </Typography>
            </div>
            <TextField
              key={'referenceString'}
              name={'referenceString'}
              disabled={isReadOnly}
              value={form.referenceString ?? ''}
              sx={{ maxWidth: '500px' }}
              fullWidth
              InputLabelProps={{ shrink: true }}
              onChange={e => setValue({ referenceString: e.target.value })}
            />
          </Box>
        </Grid>
        <Grid item md={3}>
          <Box>
            <div className={classes.mb2}>
              <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400 }}>
                Expense Code
              </Typography>
            </div>
            <FormControl key={''}>
              <AdvancedSearchComponent
                key={'expenseCode'}
                labelText={''}
                id={form.expenseCodeId}
                value={ec?.expenseCodeCode ?? ''}
                setIdValue={id => setValue({ expenseCodeId: id })}
                searchingRows={expenseCodes}
                searchingCriteria={[
                  { name: 'expenseCodeCode', title: 'Expense Code' },
                  { name: 'expenseCodeDescription', title: 'Description' },
                  { name: 'classification', title: 'Classification' },
                  { name: 'category', title: 'Category' }
                ]}
                searchingColumnExtensions={[
                  { columnName: 'expenseCodeCode', width: 150 },
                  { columnName: 'expenseCodeDescription', width: 250 },
                  { columnName: 'classification', width: 200 },
                  { columnName: 'category', width: 200 }
                ]}
                displayValueAccessorKey={'expenseCodeCode'}
                saveIdValueAccessorKey={'expenseCodeId'}
                maxWidthsx={'360px'}
                isReadOnly={isReadOnly}
              />
            </FormControl>
          </Box>
        </Grid>
        <Grid item md={3}>
          <Box>
            <div className={classes.mb2}>
              <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400 }}>
                Expense Code Desc
              </Typography>
            </div>
            <div className={classes.mb2}>
              <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, paddingTop: '10px' }}>
                {ec?.expenseCodeDescription ?? <div>&mdash;</div>}
              </Typography>
            </div>
          </Box>
        </Grid>
        <Grid item md={2}>
          <Box>
            <div className={classes.mb2}>
              <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400 }}>
                GL Number
              </Typography>
            </div>
            <TextField
              key={'glNumber'}
              name={'glNumber'}
              disabled={isReadOnly}
              value={form.glNumber ?? ''}
              sx={{ maxWidth: '500px' }}
              fullWidth
              InputLabelProps={{ shrink: true }}
              onChange={e => setValue({ glNumber: e.target.value })}
            />
          </Box>
        </Grid>
        <Grid item md={4}>
          <Box>
            <div className={classes.mb2}>
              <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400 }}>
                Denied Reason
              </Typography>
            </div>
            <TextField
              key={'deniedReason'}
              name={'deniedReason'}
              disabled={isReadOnly}
              value={form.deniedReason ?? ''}
              sx={{ maxWidth: '500px' }}
              fullWidth
              InputLabelProps={{ shrink: true }}
              onChange={e => setValue({ deniedReason: e.target.value })}
            />
          </Box>
        </Grid>
        {[
          ['reportDate', 'Report Date'],
          ['expenseDate', 'Expense Date'],
          ['payrollDate', 'Payroll Date'],
          ['dueDate', 'Due Date'],
          ['fromDate', 'From Date'],
          ['toDate', 'To Date']
        ].map(([key, label]) => (
          <Grid key={key} item md={2}>
            <Box>
              <div className={classes.mb2}>
                <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400 }}>
                  {label}
                </Typography>
              </div>
              <TextField
                key={key}
                name={key}
                disabled={isReadOnly}
                value={formatDateForInput(form[key])}
                sx={{ maxWidth: '500px' }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="date"
                onChange={e => setValue({ [key]: e.target.value })}
              />
            </Box>
          </Grid>
        ))}
        <Grid item md={2}>
          <Box>
            <div className={classes.mb2}>
              <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400 }}>
                Exception
              </Typography>
            </div>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Select
                labelId={'selectedException'}
                key={'selectedException'}
                name={'selectedException'}
                value={selectedExceptionId}
                sx={{ maxWidth: '140px', minWidth: '120px' }}
                disabled={isReadOnly}
                onChange={e => setSelectedExceptionId(e.target.value)}
              >
                <MenuItem disabled={isReadOnly} key={0} value={0} sx={{ margin: 'dense' }}>
                  None
                </MenuItem>
                {filteredExceptions.map(opt => (
                  <MenuItem disabled={isReadOnly} key={opt.exceptionId} value={opt.exceptionId} sx={{ margin: 'dense' }}>
                    {opt.commentSubject}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        </Grid>
        <Grid item md={4} sx={{ marginTop: 4, marginLeft: 2 }}>
          {!isReadOnly && (
            <Button variant="contained" color="primary" onClick={() => setOpenAddException(true)} sx={{ minWidth: 120 }}>
              Add Exception
            </Button>
          )}
        </Grid>
        <Grid item md={18}>
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', paddingTop: '24px' }}>
            {isLoading && <Spinner />}
            {!isLoading && (
              <>
                {/* <Button onClick={splitLine} sx={{ marginRight: '10px' }}>Split</Button> */}
                {!isReadOnly && (
                  <Button color="primary" variant="contained" onClick={handleSave} disabled={!isDirty}>
                    Save Line
                  </Button>
                )}
                <Button sx={{ ml: 1 }} onClick={closeLineItem}>
                  Close
                </Button>
              </>
            )}
          </Box>
        </Grid>
        <Dialog open={openAddException} onClose={() => setOpenAddException(false)} fullWidth maxWidth="md">
          <DialogTitle>Add Exception</DialogTitle>
          <DialogContent dividers>
            <SelectedCommentAndNotesComponent
              isNewComment={true}
              selectedComment={{
                authorizationCommentId: null,
                taskId: null,
                isTransfereeViewable: false,
                isClientViewable: false,
                isPinned: false,
                commentId: null,
                serviceId: null,
                regardingId: null,
                subject: '',
                comment: '',
                addedByUserId: null,
                modifiedByUserId: null,
                createdParty: null,
                isException: true,
                authorizationExceptionCommentId: null,
                exceptionReason: '',
                requestedAmount: null,
                statusId: null,
                exceptionCodeId: null,
                categoryId: null,
                requestTypeId: null,
                approvedById: null,
                approvedDate: null,
                approvedAmount: null,
                exceptionTypeId: null,
                requestedItem: null,

                addedOn: null,
                templateId: null,
                emailTemplateSentDate: null,
                exceptionApprovers: [],
                exceptionLineItems: []
              }}
              halfView={false}
              enumerations={enumerations}
              authorizationId={authId}
              defaultEmail={''}
              clientContacts={[]}
              setIsLoading={() => {}}
              setSelectedComment={() => {}}
              isAuthLineItem={true}
              onAfterCreate={(newExceptionCommentId, approved) => {
                setIsLoading(true);
                if (approved) {
                  setSelectedExceptionId(newExceptionCommentId);
                }
                setOpenAddException(false);
                setIsLoading(false);
              }}
              forceRegardingId={ExceptionId}
              setExceptionState={setExceptions}
            />
          </DialogContent>
        </Dialog>
      </Grid>
    </Box>
  );
};

export default ReportLineItem;
